import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button,
    IconButton,
    Link,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import html2pdf from "html2pdf.js";
import httpClient, { extractApiError } from "../api/httpClient";
import { PageHeader, PagePanel, PageShell, scheduleTableTypography } from "../components/PageLayout";
import ShiftModal from "../components/ShiftModal";
import { useAuth } from "../contexts/AuthContext";
import { useUI } from "../contexts/UIContext";

const FASCIA_MANSIONI = {
    PRANZO: ["CUCINA", "PIZZERIA", "CAMERIERE", "CASSA"],
    CENA: ["CUCINA", "PIZZERIA", "CAMERIERE", "CASSA", "RIDER"],
};
const PRANZO_START_MIN = 11 * 60;
const PRANZO_END_MIN = 15 * 60;
const CENA_START_MIN = 18 * 60;
const CENA_END_MIN = 23 * 60 + 30;
const WEEKDAY_KEYS = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];

const normalizeAvailabilityLocales = (availability) =>
    (availability?.localeIds || availability?.locali || []).map((entry) =>
        typeof entry === "object" ? Number(entry.id) : Number(entry)
    );

const normalizeAvailabilityRecord = (availability) => ({
    ...availability,
    tipoDisponibilita: availability?.tipoDisponibilita || "DISPONIBILITA",
    localeIds: normalizeAvailabilityLocales(availability),
});

const normalizeText = (value) => String(value || "").trim().toUpperCase();

const getDayOfWeekKey = (dateLike) => {
    const date = new Date(dateLike);
    return WEEKDAY_KEYS[date.getDay()];
};

const toMinutes = (dateLike) => {
    const d = new Date(dateLike);
    return d.getHours() * 60 + d.getMinutes();
};

const overlaps = (startA, endA, startB, endB) => startA < endB && endA > startB;

const getShiftFascia = (startDateLike, endDateLike) => {
    const start = toMinutes(startDateLike);
    const end = toMinutes(endDateLike);

    const inPranzo = overlaps(start, end, PRANZO_START_MIN, PRANZO_END_MIN);
    const inCena = overlaps(start, end, CENA_START_MIN, CENA_END_MIN);

    if (inPranzo && !inCena) {
        return "PRANZO";
    }

    if (inCena && !inPranzo) {
        return "CENA";
    }

    return start < CENA_START_MIN ? "PRANZO" : "CENA";
};

const normalizeMansione = (mansioneRaw) => {
    const value = String(mansioneRaw || "SENZA MANSIONE").toUpperCase();
    return value;
};

const toDateKey = (dateLike) => {
    const d = new Date(dateLike);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const buildDateTimeFromKey = (dateKey, hours, minutes) => {
    const [y, m, d] = dateKey.split("-").map(Number);
    const pad = (value) => String(value).padStart(2, "0");
    return `${y}-${pad(m)}-${pad(d)}T${pad(hours)}:${pad(minutes)}:00`;
};

const toIsoDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const getMonday = (baseDate = new Date()) => {
    const d = new Date(baseDate);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diffToMonday = (day + 6) % 7;
    d.setDate(d.getDate() - diffToMonday);
    return d;
};

const getWeekRange = (weekStartDate) => {
    if (!weekStartDate) {
        return null;
    }

    const start = new Date(`${weekStartDate}T00:00:00`);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return {
        start,
        end,
        startStr: toIsoDate(start),
        endStr: toIsoDate(end),
    };
};

const mapShiftsToAgendaRows = (shifts = []) => {
    const grouped = new Map();

    shifts.forEach((shift) => {
        const dateKey = toDateKey(shift.inizio);
        const fascia = getShiftFascia(shift.inizio, shift.fine);
        const mansione = normalizeMansione(shift.mansione);

        if (!grouped.has(dateKey)) {
            grouped.set(dateKey, {
                PRANZO: new Map(
                    FASCIA_MANSIONI.PRANZO.map((m) => [m, { dipendenti: new Set(), shifts: [] }])
                ),
                CENA: new Map(
                    FASCIA_MANSIONI.CENA.map((m) => [m, { dipendenti: new Set(), shifts: [] }])
                ),
            });
        }

        const dayGroup = grouped.get(dateKey);
        if (!dayGroup[fascia].has(mansione)) {
            dayGroup[fascia].set(mansione, {
                dipendenti: new Set(),
                shifts: [],
            });
        }

        const bucket = dayGroup[fascia].get(mansione);
        if (shift?.dipendente?.nome) {
            bucket.dipendenti.add(shift.dipendente.nome);
        }
        bucket.shifts.push(shift);
    });

    return Array.from(grouped.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .flatMap(([dateKey, dayGroup]) => {
            const toRows = (fascia, mansioni, baseHour) =>
                mansioni.map((mansione, index) => {
                    const bucket = dayGroup[fascia].get(mansione) || { dipendenti: new Set(), shifts: [] };
                    const dipendenti = Array.from(bucket.dipendenti);
                    const firstShift = bucket.shifts[0] || null;
                    return {
                        id: `${dateKey}-${fascia}-${mansione}`,
                        title: `${mansione} - ${dipendenti.length ? dipendenti.join(", ") : "Nessun dipendente"}`,
                        start: buildDateTimeFromKey(dateKey, baseHour, index),
                        end: buildDateTimeFromKey(dateKey, baseHour, index + 1),
                        extendedProps: {
                            fascia,
                            mansione,
                            mansioneOrder: index,
                            fasciaOrder: fascia === "PRANZO" ? 0 : 1,
                            dateKey,
                            shiftId: firstShift?.id || null,
                            employeeId: firstShift?.dipendente?.id || null,
                            localeId: firstShift?.locale?.id || null,
                            originalStart: firstShift?.inizio || null,
                            originalEnd: firstShift?.fine || null,
                            allShifts: bucket.shifts || [],
                        },
                    };
                });

            return [
                ...toRows("PRANZO", FASCIA_MANSIONI.PRANZO, 11),
                ...toRows("CENA", FASCIA_MANSIONI.CENA, 18),
            ];
        });
};

export default function Turni() {
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [availabilities, setAvailabilities] = useState([]);
    const [locali, setLocali] = useState([]);
    const [selectedLocaleId, setSelectedLocaleId] = useState("ALL");
    const [weekStartDate, setWeekStartDate] = useState(toIsoDate(getMonday()));
    const [selectedShift, setSelectedShift] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [hoveredCell, setHoveredCell] = useState(null);

    const { hasAnyRole } = useAuth();
    const { showNotification } = useUI();

    const canManage = useMemo(() => hasAnyRole(["SUPERADMIN", "ADMIN", "MANAGER"]), [hasAnyRole]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const range = getWeekRange(weekStartDate);
            let shiftsEndpoint = selectedLocaleId === "ALL" ? "/turni" : `/turni/locale/${Number(selectedLocaleId)}`;
            let shiftsConfig = undefined;

            if (range) {
                if (selectedLocaleId === "ALL") {
                    shiftsEndpoint = "/turni/settimana";
                    shiftsConfig = { params: { dataInizio: range.startStr, dataFine: range.endStr } };
                } else {
                    shiftsEndpoint = `/turni/locale/${Number(selectedLocaleId)}/settimana`;
                    shiftsConfig = { params: { dataInizio: range.startStr, dataFine: range.endStr } };
                }
            }

            const [shiftsResponse, employeesResponse, availabilitiesResponse, localiResponse] = await Promise.all([
                httpClient.get(shiftsEndpoint, shiftsConfig),
                httpClient.get("/dipendenti"),
                httpClient.get("/disponibilita"),
                httpClient.get("/locali"),
            ]);
            setEvents(mapShiftsToAgendaRows(shiftsResponse?.data || []));
            setEmployees(employeesResponse?.data || []);
            setAvailabilities((availabilitiesResponse?.data || []).map(normalizeAvailabilityRecord));
            setLocali(localiResponse?.data || []);
        } catch (error) {
            showNotification(extractApiError(error), "error");
        } finally {
            setLoading(false);
        }
    }, [showNotification, selectedLocaleId, weekStartDate]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const openCreateModal = () => {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, "0");
        const d = String(today.getDate()).padStart(2, "0");
        const dateStr = `${y}-${m}-${d}`;

        setSelectedShift({
            mansione: "",
            employeeId: "",
            localeId: selectedLocaleId === "ALL" ? "" : String(selectedLocaleId),
            date: dateStr,
            pranzo: true,
            cena: false,
        });
        setModalOpen(true);
    };

    const persistShift = async (payload) => {
        try {
            const turnoPayload = {
                dipendenteId: Number(payload.employeeId),
                localeId: Number(payload.localeId),
                inizio: payload.start,
                fine: payload.end,
                mansione: payload.mansione || undefined,
            };

            if (payload.id) {
                await httpClient.put(`/turni/${payload.id}`, turnoPayload);
                showNotification("Turno aggiornato", "success");
            } else {
                await httpClient.post("/turni", turnoPayload);
                showNotification("Turno creato", "success");
            }

            setModalOpen(false);
            setSelectedShift(null);
            await loadData();
        } catch (error) {
            showNotification(extractApiError(error), "error");
        }
    };

    const deleteShift = async (shiftId) => {
        try {
            await httpClient.delete(`/turni/${shiftId}`);
            showNotification("Turno eliminato", "success");
            setModalOpen(false);
            setSelectedShift(null);
            await loadData();
        } catch (error) {
            showNotification(extractApiError(error), "error");
        }
    };

    const handlePrint = () => {
        const element = document.getElementById("tabella-turni-container");
        if (!element) {
            showNotification("Errore: impossibile trovare la tabella", "error");
            return;
        }

        const locale = locali.find((l) => String(l.id) === selectedLocaleId);
        const localeName = locale ? locale.nome : "Tutti i locali";
        const weekRange = getWeekRange(weekStartDate);
        const startDate = new Date(weekRange.start).toLocaleDateString("it-IT");
        const filename = `turni_${localeName}_${startDate.replace(/\//g, "-")}`;

        const opt = {
            margin: 10,
            filename,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { orientation: "landscape", unit: "mm", format: "a4" },
        };

        html2pdf()
            .set(opt)
            .from(element)
            .output('bloburi')
            .then((blobUri) => {
                const printWindow = window.open(blobUri);
                if (printWindow) {
                    printWindow.print();
                }
            })
            .catch((error) => {
                showNotification("Errore nella generazione del PDF per la stampa", "error");
                console.error(error);
            });
    };

    const handleDownloadPDF = () => {
        const element = document.getElementById("tabella-turni-container");
        if (!element) {
            showNotification("Errore: impossibile trovare la tabella", "error");
            return;
        }

        const locale = locali.find((l) => String(l.id) === selectedLocaleId);
        const localeName = locale ? locale.nome : "Tutti i locali";
        const weekRange = getWeekRange(weekStartDate);
        const startDate = new Date(weekRange.start).toLocaleDateString("it-IT");
        const endDate = new Date(weekRange.end).toLocaleDateString("it-IT");
        const filename = `turni_${localeName}_${startDate.replace(/\//g, "-")}.pdf`;

        const opt = {
            margin: 10,
            filename,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { orientation: "landscape", unit: "mm", format: "a4" },
        };

        html2pdf().set(opt).from(element).save().catch((error) => {
            showNotification("Errore nel download del PDF", "error");
            console.error(error);
        });
    };

    const generateShifts = async () => {
        if (!weekStartDate) {
            showNotification("Seleziona la data di inizio settimana", "warning");
            return;
        }

        try {
            if (selectedLocaleId === "ALL") {
                await httpClient.post("/turni/genera", { dataInizioSettimana: weekStartDate });
                showNotification("Generazione turni avviata per tutti i locali", "success");
            } else {
                await httpClient.post(`/turni/locale/${Number(selectedLocaleId)}/genera`, {
                    dataInizioSettimana: weekStartDate,
                });
                showNotification("Generazione turni avviata per il locale selezionato", "success");
            }

            await loadData();
        } catch (error) {
            showNotification(extractApiError(error), "error");
        }
    };

    const getWeekDates = () => {
        const range = getWeekRange(weekStartDate);
        if (!range) return [];
        
        const dates = [];
        const current = new Date(range.start);
        for (let i = 0; i < 7; i++) {
            dates.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    const moveWeekByDays = (days) => {
        const base = weekStartDate ? new Date(`${weekStartDate}T00:00:00`) : getMonday();
        base.setDate(base.getDate() + days);
        setWeekStartDate(toIsoDate(getMonday(base)));
    };

    const getShiftsByDateAndMansione = (dateStr, fascia, mansione) => {
        return (events || []).filter(
            (evt) =>
                evt.extendedProps?.dateKey === dateStr &&
                evt.extendedProps?.fascia === fascia &&
                evt.extendedProps?.mansione === mansione
        );
    };

    const getPersonnelForDateMansione = (dateStr, fascia, mansione) => {
        const shifts = getShiftsByDateAndMansione(dateStr, fascia, mansione);
        return shifts;
    };

    const employeesById = useMemo(
        () =>
            new Map(
                employees
                    .map((employee) => [Number(employee?.id), employee])
                    .filter(([employeeId]) => Number.isFinite(employeeId))
            ),
        [employees]
    );

    const getReperibiliForDateMansione = useCallback(
        (dateStr, mansione) => {
            const dayOfWeek = getDayOfWeekKey(`${dateStr}T00:00:00`);
            const localeId = selectedLocaleId === "ALL" ? null : Number(selectedLocaleId);
            const requiredMansione = normalizeText(mansione);
            const reperibili = new Set();

            availabilities.forEach((availability) => {
                if (availability?.tipoDisponibilita !== "REPERIBILITA") {
                    return;
                }

                const availabilityDay = availability?.giornoSettimana || availability?.dayOfWeek;
                if (availabilityDay !== dayOfWeek) {
                    return;
                }

                if (localeId && !availability?.localeIds?.includes(localeId)) {
                    return;
                }

                const employeeId = Number(availability?.dipendente?.id);
                const employee = employeesById.get(employeeId);
                const employeeMansione = normalizeText(employee?.mansione);
                if (requiredMansione && employeeMansione !== requiredMansione) {
                    return;
                }

                const employeeName = availability?.dipendente?.nome || employee?.nome;
                if (employeeName) {
                    reperibili.add(employeeName);
                }
            });

            return Array.from(reperibili).sort((a, b) => a.localeCompare(b));
        },
        [availabilities, employeesById, selectedLocaleId]
    );

    const employeesAvailableForNewShift = useMemo(() => {
        if (!selectedShift) {
            return employees;
        }

        const dayOfWeek = getDayOfWeekKey(selectedShift.date);
        const requestedMansione = normalizeText(selectedShift.mansione);
        const localeId = selectedShift.localeId ? Number(selectedShift.localeId) : null;
        const wantsPranzo = Boolean(selectedShift.pranzo);
        const wantsCena = Boolean(selectedShift.cena);

        if (!dayOfWeek || (!wantsPranzo && !wantsCena)) {
            return employees;
        }

        if (!availabilities.length) {
            return requestedMansione ? employees.filter((employee) => normalizeText(employee?.mansione) === requestedMansione) : employees;
        }

        const availabilityByEmployeeId = new Map();
        availabilities.forEach((availability) => {
            const employeeId = Number(availability?.dipendente?.id);
            if (!employeeId) {
                return;
            }

            if (!availabilityByEmployeeId.has(employeeId)) {
                availabilityByEmployeeId.set(employeeId, []);
            }

            availabilityByEmployeeId.get(employeeId).push({
                ...availability,
                localeIds: normalizeAvailabilityLocales(availability),
            });
        });

        const filteredEmployees = employees.filter((employee) => {
            const employeeId = Number(employee?.id);
            const employeeMansione = normalizeText(employee?.mansione);
            const employeeAvailabilities = availabilityByEmployeeId.get(employeeId) || [];

            return employeeAvailabilities.some((availability) => {
                const matchesDay = availability.giornoSettimana === dayOfWeek || availability.dayOfWeek === dayOfWeek;
                const matchesService = (wantsPranzo && availability.pranzo) || (wantsCena && availability.cena);
                const matchesLocale = !localeId || availability.localeIds.includes(localeId);
                const matchesMansione = !requestedMansione || employeeMansione === requestedMansione;

                return matchesDay && matchesService && matchesLocale && matchesMansione;
            });
        });

        if (filteredEmployees.length > 0) {
            return filteredEmployees;
        }

        const mansioneFallback = requestedMansione
            ? employees.filter((employee) => normalizeText(employee?.mansione) === requestedMansione)
            : employees;

        return mansioneFallback.length > 0 ? mansioneFallback : employees;
    }, [availabilities, employees, selectedShift]);

    const handlePersonnelClick = (event, shift, employeeName) => {
        event.stopPropagation();
        if (!canManage) return;
        
        const fascia = shift.extendedProps?.fascia;
        const dateKey = shift.extendedProps?.dateKey;
        const allShifts = shift.extendedProps?.allShifts || [];
        
        // Trova il turno specifico per questo dipendente
        const specificShift = allShifts.find((s) => s.dipendente?.nome === employeeName) || allShifts[0];
        
        setSelectedShift({
            id: specificShift?.id || null,
            mansione: specificShift?.mansione || shift.extendedProps?.mansione,
            employeeId: specificShift?.dipendente?.id,
            localeId: specificShift?.locale?.id,
            date: dateKey,
            pranzo: fascia === "PRANZO",
            cena: fascia === "CENA",
            start: specificShift?.inizio,
            end: specificShift?.fine,
        });
        setModalOpen(true);
    };

    const handleReperibileClick = (event, employeeName, dateKey, fascia, mansione) => {
        event.stopPropagation();
        if (!canManage) return;

        const requestedMansione = normalizeText(mansione);
        const matchedEmployee =
            employees.find(
                (employee) =>
                    employee?.nome === employeeName &&
                    normalizeText(employee?.mansione) === requestedMansione
            ) || employees.find((employee) => employee?.nome === employeeName);

        const start = buildDateTimeFromKey(dateKey, fascia === "PRANZO" ? 11 : 18, 0);
        const end = buildDateTimeFromKey(dateKey, fascia === "PRANZO" ? 15 : 23, fascia === "PRANZO" ? 0 : 30);

        setSelectedShift({
            id: null,
            mansione,
            employeeId: matchedEmployee?.id || "",
            localeId: selectedLocaleId === "ALL" ? "" : String(selectedLocaleId),
            date: dateKey,
            pranzo: fascia === "PRANZO",
            cena: fascia === "CENA",
            start,
            end,
        });
        setModalOpen(true);
    };

    const renderShiftNames = (shifts, reperibili, cellKey, mansionePalette, dateKey, fascia, mansione) => {
        if (!shifts.length) {
            return (
                <div style={{ pointerEvents: "auto", minHeight: canManage ? "72px" : "56px" }}>
                    <div style={{ ...scheduleTableTypography.helper, color: "#7f8c8d", fontStyle: "italic" }}>
                        {canManage ? "Clicca per aggiungere" : ""}
                    </div>

                    {reperibili.length > 0 && (
                        <div
                            style={{
                                marginTop: "6px",
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                gap: "4px",
                                color: "var(--app-reperibilita-text-color, #e67e22)",
                            }}
                        >
                            
                            {reperibili.map((name, index) => (
                                <React.Fragment key={`${name}-${index}`}>
                                    <Link
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        color="inherit"
                                        onClick={(event) => handleReperibileClick(event, name, dateKey, fascia, mansione)}
                                        sx={{
                                            ...scheduleTableTypography.employeeName,
                                            color: "inherit",
                                            cursor: "pointer",
                                            display: "inline",
                                            p: 0,
                                            borderRadius: "2px",
                                            transition: "background-color 0.15s",
                                            ...(hoveredCell === cellKey ? { backgroundColor: mansionePalette } : {}),
                                        }}
                                    >
                                        {name}
                                    </Link>
                                    {index < reperibili.length - 1 ? ", " : ""}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        const scheduledNames = new Set();

        return (
            <div style={{ pointerEvents: "auto", minHeight: canManage ? "72px" : "56px" }}>
                {shifts.map((shift, idx) => {
                    const namesFromShifts = (shift?.extendedProps?.allShifts || [])
                        .map((item) => item?.dipendente?.nome)
                        .filter(Boolean);

                    const title = shift.title || "";
                    const match = title.match(/^[A-Z]+ - (.+)$/);
                    const namesFromTitle = (match ? match[1] : "")
                        .split(", ")
                        .filter((n) => n && n !== "Nessun dipendente");

                    const nameArray = Array.from(new Set([...namesFromShifts, ...namesFromTitle]));

                    nameArray.forEach((name) => {
                        scheduledNames.add(name);
                    });

                    return (
                        <div
                            key={idx}
                            style={{
                                pointerEvents: "auto",
                                color: "var(--app-disponibilita-text-color, var(--app-table-cell-text-color, #3498db))",
                            }}
                        >
                            {nameArray.map((name, nameIdx) => (
                                <React.Fragment key={nameIdx}>
                                    <Link
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        color="inherit"
                                        onClick={(e) => handlePersonnelClick(e, shift, name)}
                                        sx={{
                                            ...scheduleTableTypography.employeeName,
                                            color: "inherit",
                                            cursor: "pointer",
                                            display: "inline",
                                            p: 0,
                                            borderRadius: "2px",
                                            transition: "background-color 0.15s",
                                            ...(hoveredCell === cellKey ? { backgroundColor: mansionePalette } : {}),
                                        }}
                                    >
                                        {name}
                                    </Link>
                                    {nameIdx < nameArray.length - 1 ? ", " : ""}
                                </React.Fragment>
                            ))}
                            {idx < shifts.length - 1 && (
                                <div style={{ borderBottom: "1px solid #ddd", margin: "3px 0" }} />
                            )}
                        </div>
                    );
                })}

                {reperibili.filter((name) => !scheduledNames.has(name)).length > 0 && (
                    <div
                        style={{
                            marginTop: "6px",
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "4px",
                            color: "var(--app-reperibilita-text-color, #e67e22)",
                        }}
                    >
                        
                        {reperibili
                            .filter((name) => !scheduledNames.has(name))
                            .map((name, index, arr) => (
                                <React.Fragment key={`${name}-${index}`}>
                                    <Link
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        color="inherit"
                                        onClick={(event) => handleReperibileClick(event, name, dateKey, fascia, mansione)}
                                        sx={{
                                            ...scheduleTableTypography.employeeName,
                                                color: "inherit",
                                            cursor: "pointer",
                                            display: "inline",
                                            p: 0,
                                            borderRadius: "2px",
                                            transition: "background-color 0.15s",
                                            ...(hoveredCell === cellKey ? { backgroundColor: mansionePalette } : {}),
                                        }}
                                    >
                                        {name}
                                    </Link>
                                    {index < arr.length - 1 ? ", " : ""}
                                </React.Fragment>
                            ))}
                    </div>
                )}

                {canManage && (
                    <div style={{ ...scheduleTableTypography.helper, marginTop: "6px", color: "#7f8c8d" }}>
                        Spazio libero: clicca nella cella per inserire
                    </div>
                )}
            </div>
        );
    };

    const weekDates = getWeekDates();
    const weekRange = getWeekRange(weekStartDate);
    const currentLocale = locali.find((l) => String(l.id) === selectedLocaleId);
    
    const dayNames = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

    return (
        <PageShell>
            <PageHeader
                title="Pianificazione turni"
                subtitle="Agenda settimanale professionale per la gestione turni. Questa vista definisce il nuovo stile condiviso dell'applicazione."
                actions={
                    canManage ? (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModal}>
                            Nuovo turno
                        </Button>
                    ) : null
                }
            />

            <PagePanel tone="cool" sx={{ p: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                    <TextField
                        select
                        label="Filtro locale"
                        value={selectedLocaleId}
                        onChange={(event) => setSelectedLocaleId(event.target.value)}
                        sx={{ minWidth: 260 }}
                    >
                        <MenuItem value="ALL">Tutti i locali</MenuItem>
                        {locali.map((locale) => (
                            <MenuItem key={locale.id} value={String(locale.id)}>
                                {locale.nome} ({locale.codice || "N/A"})
                            </MenuItem>
                        ))}
                    </TextField>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <IconButton
                            aria-label="Settimana precedente"
                            onClick={() => moveWeekByDays(-7)}
                            size="small"
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                        <TextField
                            label="Inizio settimana"
                            type="date"
                            value={weekStartDate}
                            onChange={(event) => setWeekStartDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <IconButton
                            aria-label="Settimana successiva"
                            onClick={() => moveWeekByDays(7)}
                            size="small"
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    </Stack>

                    {canManage && (
                        <>
                            <Button variant="outlined" onClick={generateShifts}>
                                Genera turni
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                            >
                                Stampa
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadPDF}
                            >
                                Download PDF
                            </Button>
                        </>
                    )}
                    {!canManage && (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<PrintIcon />}
                                onClick={handlePrint}
                            >
                                Stampa
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadPDF}
                            >
                                Download PDF
                            </Button>
                        </>
                    )}
                </Stack>
            </PagePanel>

            <PagePanel tone="neutral" sx={{ p: 3, overflowX: "auto", "@media print": { boxShadow: "none", padding: 0 } }}>
                {loading ? (
                    <Skeleton variant="rounded" height={560} />
                ) : (
                    <div id="tabella-turni-container">
                        {/* HEADER */}
                        <Stack sx={{ mb: 3, pb: 2, borderBottom: "3px solid #2c3e50" }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: "#2c3e50" }}>
                                RISTORANTE - AGENDA TURNI SETTIMANALE
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {currentLocale ? currentLocale.nome : "Tutti i locali"}
                            </Typography>
                            {weekRange && (
                                <Typography variant="caption" sx={{ mt: 1 }}>
                                    Settimana: {new Date(weekRange.start).toLocaleDateString("it-IT", {
                                        weekday: "long",
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                    })}{" "}
                                    -{" "}
                                    {new Date(weekRange.end).toLocaleDateString("it-IT", {
                                        weekday: "long",
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                    })}
                                </Typography>
                            )}
                        </Stack>

                        {/* TABELLA */}
                        <table
                            style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                ...scheduleTableTypography.table,
                                marginBottom: "30px",
                            }}
                        >
                            <thead>
                                <tr style={{ backgroundColor: "#34495e", color: "white", fontWeight: "bold" }}>
                                    <th style={{ ...scheduleTableTypography.headerCell, padding: "12px 8px", border: "1px solid #2c3e50", textAlign: "left" }}>
                                        Fascia
                                    </th>
                                    <th style={{ ...scheduleTableTypography.headerCell, padding: "12px 8px", border: "1px solid #2c3e50", textAlign: "left" }}>
                                        Reparto
                                    </th>
                                    {dayNames.map((day, idx) => (
                                        <th
                                            key={day}
                                            style={{
                                                ...scheduleTableTypography.headerCell,
                                                padding: "12px 8px",
                                                border: "1px solid #2c3e50",
                                                textAlign: "center",
                                                minWidth: "100px",
                                            }}
                                        >
                                            {day}
                                            <br />
                                            <span style={{ ...scheduleTableTypography.sectionMeta, fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>
                                                {weekDates[idx]?.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* PRANZO */}
                                {["CUCINA", "PIZZERIA", "CAMERIERE", "CASSA"].map((mansione, idx) => (
                                    <tr
                                        key={`pranzo-${mansione}`}
                                        style={{
                                            backgroundColor: idx === 0 ? "#d6eaf8" : "inherit",
                                        }}
                                    >
                                        {idx === 0 && (
                                            <td
                                                rowSpan={4}
                                                style={{
                                                    ...scheduleTableTypography.sectionCell,
                                                    fontWeight: 600,
                                                    backgroundColor: "#d6eaf8",
                                                    border: "1px solid #bdc3c7",
                                                    borderLeft: "4px solid #3498db",
                                                    padding: "15px 8px",
                                                    textAlign: "center",
                                                    color: "#2c3e50",
                                                }}
                                            >
                                                PRANZO
                                                <br />
                                                <span style={scheduleTableTypography.sectionMeta}>(11:00 - 15:00)</span>
                                            </td>
                                        )}
                                        <td
                                            style={{
                                                ...scheduleTableTypography.sectionCell,
                                                fontWeight: 600,
                                                backgroundColor: "#ecf0f1",
                                                border: "1px solid #bdc3c7",
                                                borderLeft: "4px solid #3498db",
                                                padding: "15px 8px",
                                            }}
                                        >
                                            {mansione}
                                        </td>
                                        {weekDates.map((date, dayIdx) => {
                                            const dateStr = toDateKey(date);
                                            const shifts = getPersonnelForDateMansione(dateStr, "PRANZO", mansione);
                                            const reperibili = getReperibiliForDateMansione(dateStr, mansione);
                                            return (
                                                <td
                                                    key={`pranzo-${mansione}-${dayIdx}`}
                                                    onClick={() => {
                                                        if (canManage) {
                                                            setSelectedShift({
                                                                mansione,
                                                                employeeId: "",
                                                                localeId:
                                                                    selectedLocaleId === "ALL" ? "" : String(selectedLocaleId),
                                                                date: dateStr,
                                                                pranzo: true,
                                                                cena: false,
                                                            });
                                                            setModalOpen(true);
                                                        }
                                                    }}
                                                    onMouseEnter={() => {
                                                        if (canManage) {
                                                            setHoveredCell(`pranzo-${mansione}-${dayIdx}`);
                                                        }
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveredCell(null);
                                                    }}
                                                    style={{
                                                        ...scheduleTableTypography.bodyCell,
                                                        backgroundColor:
                                                            hoveredCell === `pranzo-${mansione}-${dayIdx}` ? "#d4edfa" : "#f0f8ff",
                                                        border:
                                                            hoveredCell === `pranzo-${mansione}-${dayIdx}`
                                                                ? "2px dashed #3498db"
                                                                : "1px solid #bdc3c7",
                                                        padding:
                                                            hoveredCell === `pranzo-${mansione}-${dayIdx}` ? "6px" : "7px",
                                                        minHeight: "78px",
                                                        verticalAlign: "top",
                                                        cursor: canManage ? "pointer" : "default",
                                                        wordWrap: "break-word",
                                                        transition: "all 0.2s ease-in-out",
                                                        boxShadow:
                                                            hoveredCell === `pranzo-${mansione}-${dayIdx}`
                                                                ? "inset 0 0 8px rgba(52, 152, 219, 0.3)"
                                                                : "none",
                                                    }}
                                                >
                                                    {renderShiftNames(
                                                        shifts,
                                                        reperibili,
                                                        `pranzo-${mansione}-${dayIdx}`,
                                                        "rgba(41, 128, 185, 0.1)",
                                                        dateStr,
                                                        "PRANZO",
                                                        mansione
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}

                                {/* SEPARATORE */}
                                <tr style={{ backgroundColor: "#2c3e50", height: "3px" }}>
                                    <td colSpan={9} style={{ padding: 0 }} />
                                </tr>

                                {/* CENA */}
                                {["CUCINA", "PIZZERIA", "CAMERIERE", "CASSA", "RIDER"].map((mansione, idx) => (
                                    <tr
                                        key={`cena-${mansione}`}
                                        style={{
                                            backgroundColor: idx === 0 ? "#fdebd0" : "inherit",
                                        }}
                                    >
                                        {idx === 0 && (
                                            <td
                                                rowSpan={5}
                                                style={{
                                                    ...scheduleTableTypography.sectionCell,
                                                    fontWeight: 600,
                                                    backgroundColor: "#fdebd0",
                                                    border: "1px solid #bdc3c7",
                                                    borderLeft: "4px solid #e67e22",
                                                    padding: "15px 8px",
                                                    textAlign: "center",
                                                    color: "#2c3e50",
                                                }}
                                            >
                                                CENA
                                                <br />
                                                <span style={scheduleTableTypography.sectionMeta}>(18:00 - 23:30)</span>
                                            </td>
                                        )}
                                        <td
                                            style={{
                                                ...scheduleTableTypography.sectionCell,
                                                fontWeight: 600,
                                                backgroundColor: "#ecf0f1",
                                                border: "1px solid #bdc3c7",
                                                borderLeft: "4px solid #e67e22",
                                                padding: "15px 8px",
                                            }}
                                        >
                                            {mansione}
                                        </td>
                                        {weekDates.map((date, dayIdx) => {
                                            const dateStr = toDateKey(date);
                                            const shifts = getPersonnelForDateMansione(dateStr, "CENA", mansione);
                                            const reperibili = getReperibiliForDateMansione(dateStr, mansione);
                                            return (
                                                <td
                                                    key={`cena-${mansione}-${dayIdx}`}
                                                    onClick={() => {
                                                        if (canManage) {
                                                            setSelectedShift({
                                                                mansione,
                                                                employeeId: "",
                                                                localeId:
                                                                    selectedLocaleId === "ALL" ? "" : String(selectedLocaleId),
                                                                date: dateStr,
                                                                pranzo: false,
                                                                cena: true,
                                                            });
                                                            setModalOpen(true);
                                                        }
                                                    }}
                                                    onMouseEnter={() => {
                                                        if (canManage) {
                                                            setHoveredCell(`cena-${mansione}-${dayIdx}`);
                                                        }
                                                    }}
                                                    onMouseLeave={() => {
                                                        setHoveredCell(null);
                                                    }}
                                                    style={{
                                                        ...scheduleTableTypography.bodyCell,
                                                        backgroundColor:
                                                            hoveredCell === `cena-${mansione}-${dayIdx}` ? "#fedaa3" : "#fef9e7",
                                                        border:
                                                            hoveredCell === `cena-${mansione}-${dayIdx}`
                                                                ? "2px dashed #e67e22"
                                                                : "1px solid #bdc3c7",
                                                        padding:
                                                            hoveredCell === `cena-${mansione}-${dayIdx}` ? "6px" : "7px",
                                                        minHeight: "78px",
                                                        verticalAlign: "top",
                                                        cursor: canManage ? "pointer" : "default",
                                                        wordWrap: "break-word",
                                                        transition: "all 0.2s ease-in-out",
                                                        boxShadow:
                                                            hoveredCell === `cena-${mansione}-${dayIdx}`
                                                                ? "inset 0 0 8px rgba(230, 126, 34, 0.3)"
                                                                : "none",
                                                    }}
                                                >
                                                    {renderShiftNames(
                                                        shifts,
                                                        reperibili,
                                                        `cena-${mansione}-${dayIdx}`,
                                                        "rgba(230, 126, 34, 0.1)",
                                                        dateStr,
                                                        "CENA",
                                                        mansione
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </PagePanel>

            <ShiftModal
                open={isModalOpen && canManage}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedShift(null);
                }}
                onSave={persistShift}
                onDelete={deleteShift}
                employees={employeesAvailableForNewShift}
                locali={locali}
                shift={selectedShift}
            />
        </PageShell>
    );
}
